import { Component, OnInit, inject } from '@angular/core';
import { ChartOptions, ChartDataset } from "chart.js";
import { AppConfig } from "app/app.config";
import { LoaderService } from "app/services/loader.service";

import { BlobDownload } from "app/services/blob-download.service";
import { APIService } from "app/services/api.service";
import { Subscription } from "rxjs";
import { BaseChartDirective } from 'ng2-charts';
import { NgStyle, DatePipe } from '@angular/common';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';

type financialStatementsItemType = {
  Title: string;
  Description: string;
  Link: string;
  Date: string;
};

type financialStatementsType = {
  Items: financialStatementsItemType[];
  TotalItems: number;
};

type reportConfigType = {
  ManualSortOrder: number;
  ReportElement: string;
  ReportValue: string;
};

type dataType = {
  color: string;
  data: {
    link: string;
    title: string;
  }[];
  title: string;
};

type assetAllocationLegendType = {
  data1?: dataType[];
  data2?: dataType[];
  description?: string;
  title?: string;
};

@Component({
    selector: 'nwm-resource-planning',
    templateUrl: './resource-planning.component.html',
    styleUrls: [`./resource-planning.component.scss`],
    imports: [BaseChartDirective, NgStyle, NgbPagination, DatePipe]
})
export class ResourcePlanningComponent implements OnInit {
  private config = inject(AppConfig);
  private loaderService = inject(LoaderService);
  private api = inject(APIService);
  private BlobDownload = inject(BlobDownload);

  doughnutChartDatasets: ChartDataset[] = [{data: []}];
  doughnutChartLabels: string[] = [];
  doughnutChartOptions: ChartOptions = {};

  isChartDataAvailable = false;
  sub?: Subscription;

  assetSubclassChartColors: reportConfigType[] = [];
  assetAllocationLegend: assetAllocationLegendType = {};

  page = 1;
  pageSize: number = this.config.resources.pageSize;
  sortField: string[] = ['Date', 'Title'];
  sortDirection: string[] = ['Descending', 'Ascending'];
  clientId?: number;
  pagedFinancialStatements?: financialStatementsType;
  totalPages?: number;
  _contentBaseUrl: string = this.config.resources.contentBaseUrl;

  ngOnInit(): void {
    this.loaderService.display(true, this.constructor.name);
    this.isChartDataAvailable = false;

    // order matters, which is why we use a tuple over a hash
    const assetAllocationTuples: [string, number][] = [
      ['Real Estate', 26],
      ['Private Debt', 10],
      ['Foreign Equities', 13.5],
      ['Bonds', 9.5],
      ['Cash', 1],
      ['Private Equity', 17],
      ['Mortgages', 8.5],
      ['Canadian Equity', 7.5],
      ['High Yield Bonds', 6],
      ['Alternative Strategies', 1]
    ];
    const labels: string[] = assetAllocationTuples.map(r => r[0]);

    const assetAllocationColor: string[] = [];

    this.api.get<reportConfigType[]>(`${this.config.resources.reportConfigUrl}/${this.config.portfolio.chart.reportName}/${this.config.portfolio.chart.reportItem}`).subscribe(res => {
        this.assetSubclassChartColors = res;

        for (let i = 0; i < labels.length; i++) {
          const chartConfig = this.assetSubclassChartColors.find(assetAllocationType => assetAllocationType.ReportElement === labels[i]);

          if (chartConfig) {
            const color = chartConfig.ReportValue;
            assetAllocationColor.push(color);
          }
        }

        assetAllocationColor.push('#d6d6d6'); // adds 1 extra color at the end for some reason...

        const datasets: ChartDataset[] = [
          {
            data: assetAllocationTuples.map(r => r[1]),
            backgroundColor: assetAllocationColor,
            borderWidth: 0
          }];

        this.doughnutChartLabels = labels;
        this.doughnutChartDatasets = datasets;

        this.doughnutChartOptions.plugins = {tooltip:{enabled: true, callbacks: {
          label: (a)=> a.label,
          title: ()=> ''
        }}};

        this.isChartDataAvailable = true;

        this.configureLegend();
        this.loaderService.display(false, this.constructor.name);
      },
      () => {
        this.loaderService.display(false, this.constructor.name)
      }
    );
    this.getFinancialStatements();
  }

  configureLegend(): void {
    this.assetAllocationLegend = {
      title: 'Nicola Wealth Asset Allocation Mix (Balanced Portfolio)',
      description: 'Click the links below to open a detailed PDF about that individual asset. PDFs will open in a new window.',
      data1: [
        {
          title: 'Real Estate',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Real Estate')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/canadian-real-estate-limited-partnership.pdf',
              title: 'Nicola Canadian Real Estate Limited Partnership'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/diversified-real-estate.pdf',
              title: 'Nicola Diversified Real Estate Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/global-real-estate.pdf',
              title: 'Nicola Global Real Estate Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/us-real-estate-limited-partnership.pdf',
              title: 'Nicola U.S. Real Estate Limited Partnership (USD)'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/value-add-real-estate-limited-partnership.pdf',
              title: 'Nicola Value Add Real Estate Limited Partnership'
            }
          ]
        },
        {
          title: 'Private Debt',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Private Debt')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/private-debt.pdf',
              title: 'Nicola Private Debt Fund'
            }
          ]
        },
        {
          title: 'Foreign Equities',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Foreign Equities')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/global-small-cap.pdf',
              title: 'Nicola Global Small Cap Equity Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/international-leaders.pdf',
              title: 'Nicola International Leaders Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/sustainable-innovation.pdf',
              title: 'Nicola Sustainable Innovation Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/us-equity-income.pdf',
              title: 'Nicola U.S. Equity Income Fund (USD)'
            }
          ]
        },
        {
          title: 'Bonds',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Bonds')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/bond-fund.pdf',
              title: 'Nicola Bond Fund',
            }
          ]
        },
        {
          title: 'Cash',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Cash')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/money-market.pdf',
              title: 'Nicola Money Market Fund',
            }
          ]
        },
      ],
      data2: [
        {
          title: 'Private Equity',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Private Equity')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/diversified-private-assets.pdf',
              title: 'Nicola Diversified Private Assets Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/global-infrastructure.pdf',
              title: 'Nicola Global Infrastructure Limited Partnership'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/private-equity.pdf',
              title: 'Nicola Private Equity Limited Partnership'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/venture-capital.pdf',
              title: 'Nicola Venture Capital Limited Partnership'
            },
          ]
        },
        {
          title: 'Mortgages',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Mortgages')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/canadian-mortgage.pdf',
              title: 'Nicola Canadian Mortgage Fund'
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/us-mortgage.pdf',
              title: 'Nicola U.S. Mortgage Fund (USD)'
            }
          ]
        },
        {
          title: 'Canadian Equity',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Canadian Equity')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/canadian-equity-income.pdf',
              title: 'Nicola Canadian Equity Income Fund'
            }
          ]
        },
        {
          title: 'High Yield Bonds',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'High Yield Bonds')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/high-yield.pdf',
              title: 'Nicola High Yield Bond Fund'
            }
          ]
        },
        {
          title: 'Core Portfolio',
          color: '#d6d6d6',
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/core-portfolio.pdf',
              title: 'Nicola Core Portfolio Fund'
            }
          ]
        },
        {
          title: 'Alternative Strategies',
          color: this.assetSubclassChartColors.find(x => x.ReportElement === 'Alternative Strategies')!.ReportValue,
          data: [
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/credit-opportunities-fund.pdf',
              title: 'Nicola Credit Opportunities Fund'   
            },
            {
              link: 'https://webfiles.nicolawealth.com/one-pagers/credit-opportunities-lp.pdf',
              title: 'Nicola Credit Opportunities LP'
            }
          ]
        }
      ]
    }
  }

  getFinancialStatements() {
    this.loaderService.display(true, this.constructor.name);
    const filterProperty: string[] = [];
    const filterOperator: string[] = [];
    const filterValue: string[] = [];

    filterProperty.push('Id');
    filterOperator.push('GreaterThanOrEqual');
    filterValue.push('1');

    filterProperty.push('IsActive');
    filterOperator.push('Equals');
    filterValue.push('true');

    const query = {
      Page: this.page,
      PageSize: this.pageSize,
      SortFields: this.sortField,
      SortDirections: this.sortDirection,
      FilterProperty: filterProperty,
      FilterOperator: filterOperator,
      Filtertype: [],
      FilterValue: filterValue
    };

    this.api.postMultipart<financialStatementsType>(this.config.resources.financialStatementsUrl, query).subscribe(
      data => {
        this.pagedFinancialStatements = data;
        this.totalPages = Math.ceil(this.pagedFinancialStatements.TotalItems / this.pageSize);
        this.loaderService.display(false, this.constructor.name);
      },
      () => this.loaderService.display(false, this.constructor.name)
    );
  }

  loadPage() {
    this.getFinancialStatements();
  }

  handleDownload(path: string) {
    const fl = path.lastIndexOf("/");
    const fileName = path.substring(fl + 1);
    const fp = path.lastIndexOf(".");
    const fileFormat = path.substring(fp + 1);
    this.api.getBlob({ path: path }).subscribe(
      res => {
        this.BlobDownload.open(res, fileName, fileFormat, false);
      },
      () => this.loaderService.display(false, this.constructor.name)
    );
  }
}
